// The main entry point for the SPA

import { useEffect, useCallback, useRef } from "react"
import { Route, Navigate, Routes, useNavigate, useLocation } from "react-router"
import { useDispatch, useSelector, shallowEqual } from "react-redux"

// import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { TouchBackend } from "react-dnd-touch-backend"
import { DndProvider, TouchTransition, MouseTransition } from "react-dnd-multi-backend"

import DOMPurify from "dompurify"
import "./App.scss"
import { Settings } from 'luxon'

import { CustomDragLayer, Loading, ChatBot, Popup } from "components"
import AnalysisNavOverlay from "components/Loading/AnalysisNavOverlay"
import AnalysisTaskHost from "components/Config/AnalysisTaskHost"
import SetupScreen from "screens/SetupScreen"
import ScheduleScreen from "screens/ScheduleScreen"
import SingleMemberScheduleScreen from "screens/ScheduleScreen/SingleMemberScheduleScreen"
import ManageScreen from "screens/ManageScreen"
import LoginScreen from "screens/LoginScreen"
import AssetViewScreen from "screens/AssetViewScreen"
import SampleViewScreen from "screens/SampleViewScreen"
import ScheduleScreenMobile from "screens/ScheduleScreenMobile"
import AdministratorScreen from "screens/AdministratorScreen"
import CustomerAdministratorScreen from "screens/CustomerAdministratorScreen"
//import ScrumViewScreen from "screens/ScrumViewScreen"

import ProjectViewScreen from "screens/ProjectViewScreen"

import { loading as loadingState, teamStatus, auth, librarian, repositories } from "state_management"
import store from "store"

import isOnMobile from "utils/scripts/isOnMobile"
import LibrarianScreen from "screens/LibrarianScreen"
import LimsScreen from "screens/LimsScreen"
import HelpScreen from "screens/HelpScreen"
import useInactivityTracker from "utils/hooks/useInactivityTracker"
import useT from "utils/hooks/useT"
import { displayWithLineBreaks } from "utils/scripts/globalMessage"

const HTML5toTouch = {
  backends: [
    {
      id: "html5",
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: "touch",
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
}



export default function App() {
  const { t: tc, i18n } = useT("common")
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isCustomerAdmin = useSelector((state) => state.auth.role) === "customer_admin";
  const isPlanner = useSelector((state) => state.auth.role) === "planner";
  const isOnlyLibrarian = useSelector((state) => state.auth.isOnlyLibrarian);
  const isAdmin = useSelector((state) => state.auth.role) === "admin";
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const plannertechLanguage = useSelector((state) => state.auth.plannertechLanguage || "en");
  const IAPisRunning = useSelector((state) => state.waiting.iapLoading);
  const FillInisRunning = useSelector((state) => state.waiting.fillInLoading);
  const sampleAllocationIsRunning = useSelector((state) => state.waiting.sampleAllocating);
  const changingIncludeToday = useSelector((state) => state.teamStatus.changingIncludeToday);
  const plannertechIsReady = useSelector((state) => loadingState.isReady(state.loading, state, location.pathname));
  const showProjects = useSelector((state) => state.teamStatus.showProjects);
  const showAnalyses = useSelector((state) => state.teamStatus.showAnalyses);
  const showLibrarian = useSelector((state) => state.teamStatus.showLibrarian);
  const showLims = useSelector((state) => state.teamStatus.showLims);
  const unplanningProjects = useSelector((state) => state.projectActions.unplanning);
  const unplanningAnalyses = useSelector((state) => state.analyses.unplanning);
  const isUnplanning = unplanningAnalyses || unplanningProjects;
  const hasCompany = useSelector((state) => state.auth.companyId);
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone);
  const isMember = useSelector((state) => state.auth.isMember);
  const isViewOnly = useSelector((state) => state.auth.isViewOnly);
  const memberId = useSelector((state) => state.auth.memberId);
  const directToMemberPlan = useSelector((state) => state.teamStatus.directToMemberPlan);
  const onlyPlannertech = (showProjects || showAnalyses) && !showLibrarian;
  const onlyLibrarian = useSelector((state) => state.teamStatus.onlyLibrarian);
  const processingRepositories = useSelector((state) => {
    const { repositories, globalRepositories } = state.repositories;
    return [...repositories, ...globalRepositories]
      .filter((repo) => repo.isProcessing)
      .map((repo) => repo.id);
  }, shallowEqual);



  const showGlobalMessage = useSelector((state) => state.auth.showGlobalMessage);
  const globalMessage = useSelector((state) => state.auth.globalMessage);

  let sanitizedGlobalMessageHTML;
  if (showGlobalMessage) {
    const sanitizedGlobalMessage = DOMPurify.sanitize(globalMessage);
    sanitizedGlobalMessageHTML = displayWithLineBreaks(sanitizedGlobalMessage);
  }

  useEffect(() => {
    document.title = location.pathname === "/librarian" ? tc("qcLibrarian") : tc("plannertech")
  }, [location.pathname, tc])

  // Re-bootstrap when navigating to a route whose readiness check isn't satisfied
  // (e.g. /librarian only ran the lightweight fetch; navigating to / needs the full bootstrap).
  // Skip the first render — the initial dispatch happens in src/index.jsx.
  const hasMountedRef = useRef(false)
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    if (!isLoggedIn || isAdmin || isCustomerAdmin) return
    if (plannertechIsReady) return
    dispatch(loadingState.fetchAll(location.pathname))
  }, [location.pathname, isLoggedIn, isAdmin, isCustomerAdmin, plannertechIsReady, dispatch])

  useEffect(() => {
    if (!isLoggedIn) return
    const normalizedLanguage = (plannertechLanguage || "en").slice(0, 2)
    if (normalizedLanguage !== i18n.language?.slice(0, 2)) {
      i18n.changeLanguage(normalizedLanguage)
    }
  }, [plannertechLanguage, i18n, isLoggedIn])
  // Redirect if logged in and on /login
  useEffect(() => {
    if (isLoggedIn && location.pathname === "/login") {
      if (isAdmin || isCustomerAdmin) {
        navigate("/locations");
      } else {
        dispatch(teamStatus.fetchCurrent()).then((res) => {
          if (isOnlyLibrarian || onlyLibrarian) {
            navigate("/librarian");
          } else if (isMember && res.options.directToMemberPlan) {
            const memberData = store.getState().members[memberId]
            navigate(memberData?.showOnPlan === true ? "/member/" + memberId : "/");
          } else {
            navigate("/");
          }
        });
      }
    }
  }, [isLoggedIn, location.pathname, navigate, isAdmin, isCustomerAdmin, isOnlyLibrarian, onlyLibrarian, isMember, memberId, directToMemberPlan, dispatch]);

  // Fetch current team status every 4 seconds (if logged in)
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (isLoggedIn && !isAdmin && !isCustomerAdmin && document.visibilityState === "visible") {
        dispatch(teamStatus.fetchCurrent());
        if (isPlanner) {
          dispatch(librarian.fetchRepositories());
        }
      } else if (isLoggedIn && (isAdmin || isCustomerAdmin)) {
        if (processingRepositories.length > 0) {
          dispatch(repositories.fetchProcessing({ repositoryIds: processingRepositories }));
        }

      }
    }, 4000);
    return () => window.clearInterval(interval);
  }, [dispatch, isLoggedIn, isAdmin, isPlanner, isCustomerAdmin, processingRepositories]);

  const handleInactivity = useCallback(() => {
    if (isLoggedIn) {
      dispatch(auth.logout());
    }
  }, [dispatch, isLoggedIn]);

  useInactivityTracker(handleInactivity, isLoggedIn, 8 * 60 * 60 * 1000);
  Settings.defaultZoneName = teamTimeZone;

  // The set-password link from a reset or welcome mail joins the logged-out branch, because it has
  // to answer in BOTH states: logged out it runs the flow with the code filled in, logged in it
  // refuses outright. Hence the path in the condition rather than only in the routes — a signed-in
  // visitor must reach it too, and it cannot be left to the `*` fallback below, which navigates to
  // /login and DROPS the query string, losing the code before anything can read it.
  const isSetPasswordLink = location.pathname === "/set-password";
  if (isSetPasswordLink || !isLoggedIn) {
    return (
      <Routes>
        <Route path="/set-password" element={<LoginScreen fromSetPasswordLink />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  } else if (isCustomerAdmin) {
    return (
      <>
        <Routes>
          <Route path="/customers/*" element={<CustomerAdministratorScreen />} />
          {hasCompany && <Route path="/locations/*" element={<AdministratorScreen />} />}
          <Route path="/help/*" element={<HelpScreen />} />
          <Route path="*" element={<Navigate to="/customers" />} />
        </Routes>
        {!isOnMobile && <ChatBot message={(message) => message} />}
      </>
    );
  } else if (isAdmin) {
    return (
      <>
        <Routes>
          <Route path="/locations/*" element={<AdministratorScreen />} />
          <Route path="/help/*" element={<HelpScreen />} />
          <Route path="*" element={<Navigate to="/locations" />} />
        </Routes>
        {!isOnMobile && <ChatBot message={(message) => message} />}
      </>
    );
  } else if (!plannertechIsReady) {
    return (
      <div className="AppLoading">
        <Loading />
      </div>
    );
  } else if ((isOnlyLibrarian || onlyLibrarian) && !isViewOnly) {
    return (
      <DndProvider options={HTML5toTouch}>
        <Routes>
          <Route path="/librarian/*" element={<LibrarianScreen />} />
          <Route path="/help/*" element={<HelpScreen />} />
          <Route path="*" element={<Navigate to="/librarian" />} />
        </Routes>
        {!isOnMobile && <ChatBot message={(message) => message} />}
      </DndProvider>
    );
  }

  // Main routes
  return (
    <DndProvider options={HTML5toTouch}>
      <Routes>
        {!onlyPlannertech && !isViewOnly && <Route path="/librarian/*" element={<LibrarianScreen />} />}
        {showLims && <Route path="/lims/*" element={<LimsScreen />} />}
        <Route exact path="/" element={isOnMobile ? <ScheduleScreenMobile /> : <ScheduleScreen />} />
        {showAnalyses && <Route path="/sample-view" element={<SampleViewScreen />} />}
        <Route path="/asset-view" element={<AssetViewScreen />} />
        {showProjects && <Route path="/project-view" element={<ProjectViewScreen />} />}
        <Route path="/setup/*" element={<SetupScreen />} />
        <Route path="/help/*" element={<HelpScreen />} />
        <Route exact path="/member/:memberId" element={isOnMobile ? <ScheduleScreenMobile /> : <SingleMemberScheduleScreen view="week-view" />} />
        <Route exact path="/member/:memberId/day" element={<SingleMemberScheduleScreen view="day-view" />} />
        <Route exact path="/member/:memberId/weekend" element={<SingleMemberScheduleScreen view="weekend-view" />} />
        <Route path="/navigate" element={<ManageScreen />} />
      </Routes>
      {!isOnMobile && !isViewOnly && <ChatBot message={(message) => message} />}
      {showGlobalMessage && (
        <Popup className="plannertech-welcome-info-message" onCancel={() => dispatch(auth.changeShowGlobalMessage())}>
          <div dangerouslySetInnerHTML={{ __html: sanitizedGlobalMessageHTML }} />
        </Popup>
      )}
      <CustomDragLayer />
      {(IAPisRunning || sampleAllocationIsRunning || FillInisRunning) && (
        <div className="IapLoading">
          <Loading title={IAPisRunning ? IAPisRunning : FillInisRunning ? FillInisRunning : tc("sampleAllocation")} />
        </div>
      )}
      {isUnplanning && (
        <div className="IapLoading">
          <Loading title={isUnplanning ? tc("unplanning") : changingIncludeToday === 1 ? tc("includingToday") : tc("excludingToday")} />
        </div>
      )}
      {/* RR5 P7: brief loading overlay while a programmatic "open analysis" request resolves. */}
      <AnalysisNavOverlay />
      {/* RR8 #4061: route-independent host for `visual.activeAnalysisTaskId`, so "Go to analysis"
          opens the task dialog over the CURRENT view instead of routing to the plan first. */}
      <AnalysisTaskHost />
    </DndProvider>
  );
}
